import type {
  BedStatus,
  BedWithLocation,
  HospitalBed,
  HospitalRoom,
  HospitalStay,
  HospitalWard,
  StayStatus,
} from "./types";
import { readJson, writeJson, getStorageItem, setStorageItem } from "@/core/tenant/hospitol-storage";
import { shouldAutoSeedDemoData } from "@/core/demo/is-demo-mode-enabled";
import {
  apiAssignHospitalStayBed,
  apiDischargeHospitalStay,
  apiListHospitalBeds,
  apiListHospitalRooms,
  apiListHospitalStays,
  apiListHospitalWards,
  apiSyncHospitalBed,
  apiSyncHospitalRoom,
  apiSyncHospitalStay,
  apiSyncHospitalWard,
} from "@/core/hospitol-api-client";

export type {
  BedStatus,
  BedWithLocation,
  HospitalBed,
  HospitalRoom,
  HospitalStay,
  HospitalWard,
  StayStatus,
} from "./types";
export { BED_STATUS_LABELS, bedDisplayLabel, STAY_STATUS_LABELS } from "./types";

const WARDS_KEY = "hospitol.hospitalization.wards";
const ROOMS_KEY = "hospitol.hospitalization.rooms";
const BEDS_KEY = "hospitol.hospitalization.beds";
const STAYS_KEY = "hospitol.hospitalization.stays";
const DEMO_SEED_KEY = "hospitol.hospitalization.demoSeeded";

function normalizeWard(raw: Record<string, unknown>): HospitalWard {
  return {
    id: String(raw.id ?? ""),
    code: String(raw.code ?? ""),
    name: String(raw.name ?? ""),
  };
}

function normalizeRoom(raw: Record<string, unknown>): HospitalRoom {
  return {
    id: String(raw.id ?? ""),
    wardId: String(raw.wardId ?? ""),
    roomNumber: String(raw.roomNumber ?? ""),
  };
}

function normalizeBed(raw: Record<string, unknown>): HospitalBed {
  return {
    id: String(raw.id ?? ""),
    roomId: String(raw.roomId ?? ""),
    label: String(raw.label ?? ""),
    status: (raw.status as BedStatus) ?? "available",
    currentStayId: raw.currentStayId ? String(raw.currentStayId) : null,
  };
}

function normalizeStay(raw: Record<string, unknown>): HospitalStay {
  return {
    id: String(raw.id ?? ""),
    patientId: String(raw.patientId ?? ""),
    admissionId: raw.admissionId ? String(raw.admissionId) : null,
    bedId: raw.bedId ? String(raw.bedId) : null,
    wardName: String(raw.wardName ?? ""),
    status: (raw.status as StayStatus) ?? "pending",
    admittedAt: String(raw.admittedAt ?? new Date().toISOString()),
    bedAssignedAt: raw.bedAssignedAt ? String(raw.bedAssignedAt) : null,
    dischargedAt: raw.dischargedAt ? String(raw.dischargedAt) : null,
    attendingPractitionerId: raw.attendingPractitionerId
      ? String(raw.attendingPractitionerId)
      : null,
    attendingPractitioner: String(raw.attendingPractitioner ?? ""),
    motif: String(raw.motif ?? ""),
  };
}

function readWards(): HospitalWard[] {
  return readJson<Record<string, unknown>[]>(WARDS_KEY, []).map(normalizeWard);
}

function readRooms(): HospitalRoom[] {
  return readJson<Record<string, unknown>[]>(ROOMS_KEY, []).map(normalizeRoom);
}

function readBeds(): HospitalBed[] {
  return readJson<Record<string, unknown>[]>(BEDS_KEY, []).map(normalizeBed);
}

function readStays(): HospitalStay[] {
  return readJson<Record<string, unknown>[]>(STAYS_KEY, []).map(normalizeStay);
}

function writeBeds(beds: HospitalBed[]): void {
  writeJson(BEDS_KEY, beds);
}

function writeWards(wards: HospitalWard[]): void {
  writeJson(WARDS_KEY, wards);
}

function writeRooms(rooms: HospitalRoom[]): void {
  writeJson(ROOMS_KEY, rooms);
}

function writeStays(stays: HospitalStay[]): void {
  writeJson(STAYS_KEY, stays);
}

/** Pull structure from API (WP-HOSP1); push local if remote empty. Stays remain LS-only. */
export async function syncHospitalStructureFromApi(): Promise<boolean> {
  const [remoteWards, remoteRooms, remoteBeds] = await Promise.all([
    apiListHospitalWards(),
    apiListHospitalRooms(),
    apiListHospitalBeds(),
  ]);
  if (remoteWards === null || remoteRooms === null || remoteBeds === null) return false;

  if (remoteWards.length > 0 || remoteRooms.length > 0 || remoteBeds.length > 0) {
    writeWards(remoteWards.map((w) => normalizeWard(w as unknown as Record<string, unknown>)));
    writeRooms(remoteRooms.map((r) => normalizeRoom(r as unknown as Record<string, unknown>)));
    writeBeds(remoteBeds.map((b) => normalizeBed(b as unknown as Record<string, unknown>)));
    return true;
  }

  const localWards = readWards();
  const localRooms = readRooms();
  const localBeds = readBeds();
  for (const w of localWards) void apiSyncHospitalWard(w);
  for (const r of localRooms) void apiSyncHospitalRoom(r);
  for (const b of localBeds) void apiSyncHospitalBed(b);
  return true;
}

/** Pull stays from API (WP-HOSP2); push local if remote empty. */
export async function syncHospitalStaysFromApi(): Promise<boolean> {
  const remote = await apiListHospitalStays();
  if (remote === null) return false;
  if (remote.length > 0) {
    writeStays(remote.map((s) => normalizeStay(s as unknown as Record<string, unknown>)));
    return true;
  }
  for (const stay of readStays()) void apiSyncHospitalStay(stay);
  return true;
}

export class HospitalizationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "HospitalizationError";
  }
}

export function ensureDemoHospitalization() {
  if (!shouldAutoSeedDemoData()) return;
  if (getStorageItem(DEMO_SEED_KEY)) return;

  const wards: HospitalWard[] = [
    { id: "ward-med-a", code: "MED-A", name: "Médecine A" },
    { id: "ward-chir-b", code: "CHIR-B", name: "Chirurgie B" },
    { id: "ward-mat", code: "MAT", name: "Maternité" },
  ];
  writeJson(WARDS_KEY, wards);

  const rooms: HospitalRoom[] = [
    { id: "room-med-201", wardId: "ward-med-a", roomNumber: "201" },
    { id: "room-med-202", wardId: "ward-med-a", roomNumber: "202" },
    { id: "room-chir-301", wardId: "ward-chir-b", roomNumber: "301" },
    { id: "room-mat-101", wardId: "ward-mat", roomNumber: "101" },
  ];
  writeJson(ROOMS_KEY, rooms);

  const beds: HospitalBed[] = [
    { id: "bed-201a", roomId: "room-med-201", label: "A", status: "available", currentStayId: null },
    { id: "bed-201b", roomId: "room-med-201", label: "B", status: "available", currentStayId: null },
    { id: "bed-202a", roomId: "room-med-202", label: "A", status: "available", currentStayId: null },
    { id: "bed-301a", roomId: "room-chir-301", label: "A", status: "available", currentStayId: null },
    { id: "bed-101a", roomId: "room-mat-101", label: "A", status: "available", currentStayId: null },
  ];
  writeJson(BEDS_KEY, beds);
  writeJson(STAYS_KEY, []);

  setStorageItem(DEMO_SEED_KEY, new Date().toISOString());
}

export function listHospitalWards(): HospitalWard[] {
  ensureDemoHospitalization();
  return readWards();
}

export function listHospitalStays(): HospitalStay[] {
  ensureDemoHospitalization();
  return readStays().sort(
    (a, b) => new Date(b.admittedAt).getTime() - new Date(a.admittedAt).getTime(),
  );
}

export function listActiveStays(): HospitalStay[] {
  return listHospitalStays().filter((s) => s.status === "pending" || s.status === "active");
}

export function getStayById(id: string): HospitalStay | null {
  return listHospitalStays().find((s) => s.id === id) ?? null;
}

export function getStayByAdmission(admissionId: string): HospitalStay | null {
  return listHospitalStays().find((s) => s.admissionId === admissionId) ?? null;
}

/** Démo / rattrapage — date de début des nuitées facturées. */
export function patchStayBedAssignedAt(stayId: string, bedAssignedAt: string): HospitalStay | null {
  const stays = readStays();
  const index = stays.findIndex((s) => s.id === stayId);
  if (index < 0) return null;
  stays[index] = { ...stays[index]!, bedAssignedAt };
  writeStays(stays);
  return stays[index]!;
}

export function listStaysByPatient(patientId: string): HospitalStay[] {
  return listHospitalStays().filter((s) => s.patientId === patientId);
}

export function listBedsWithLocation(wardId?: string): BedWithLocation[] {
  ensureDemoHospitalization();
  const wards = readWards();
  const rooms = readRooms();
  const beds = readBeds();
  const stays = readStays();

  return beds
    .map((bed) => {
      const room = rooms.find((r) => r.id === bed.roomId);
      const ward = room ? wards.find((w) => w.id === room.wardId) : undefined;
      if (!room || !ward) return null;
      if (wardId && ward.id !== wardId) return null;
      const stay = bed.currentStayId
        ? stays.find((s) => s.id === bed.currentStayId) ?? null
        : null;
      return { bed, room, ward, stay };
    })
    .filter((row): row is BedWithLocation => row != null)
    .sort((a, b) => {
      const wardCmp = a.ward.name.localeCompare(b.ward.name, "fr");
      if (wardCmp !== 0) return wardCmp;
      const roomCmp = a.room.roomNumber.localeCompare(b.room.roomNumber, "fr", { numeric: true });
      if (roomCmp !== 0) return roomCmp;
      return a.bed.label.localeCompare(b.bed.label, "fr");
    });
}

export function listAvailableBeds(wardId?: string): BedWithLocation[] {
  return listBedsWithLocation(wardId).filter((row) => row.bed.status === "available");
}

export function createStayFromAdmission(input: {
  patientId: string;
  admissionId: string;
  motif: string;
  wardName?: string;
}): HospitalStay {
  const existing = getStayByAdmission(input.admissionId);
  if (existing && existing.status !== "discharged") return existing;

  const now = new Date().toISOString();
  const stay: HospitalStay = {
    id: `stay-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    patientId: input.patientId,
    admissionId: input.admissionId,
    bedId: null,
    wardName: input.wardName?.trim() || "Hospitalisation",
    status: "pending",
    admittedAt: now,
    bedAssignedAt: null,
    dischargedAt: null,
    attendingPractitionerId: null,
    attendingPractitioner: "",
    motif: input.motif.trim(),
  };

  const stays = readStays();
  stays.unshift(stay);
  writeStays(stays);
  // Upsert with local id so assign/discharge hit the same durable row.
  void apiSyncHospitalStay(stay);
  return stay;
}

export function assignBedToStay(stayId: string, bedId: string): HospitalStay {
  const stays = readStays();
  const beds = readBeds();
  const stayIndex = stays.findIndex((s) => s.id === stayId);
  const bedIndex = beds.findIndex((b) => b.id === bedId);
  if (stayIndex < 0 || bedIndex < 0) {
    throw new HospitalizationError("Séjour ou lit introuvable.");
  }

  const bed = beds[bedIndex]!;
  if (bed.status !== "available" && bed.currentStayId !== stayId) {
    throw new HospitalizationError("Ce lit n'est pas disponible.");
  }

  const stay = stays[stayIndex]!;
  if (stay.bedId && stay.bedId !== bedId) {
    const oldBedIndex = beds.findIndex((b) => b.id === stay.bedId);
    if (oldBedIndex >= 0) {
      beds[oldBedIndex] = {
        ...beds[oldBedIndex]!,
        status: "available",
        currentStayId: null,
      };
    }
  }

  const location = listBedsWithLocation().find((row) => row.bed.id === bedId);
  const now = new Date().toISOString();
  beds[bedIndex] = {
    ...bed,
    status: "occupied",
    currentStayId: stayId,
  };
  stays[stayIndex] = {
    ...stay,
    bedId,
    wardName: location?.ward.name ?? stay.wardName,
    status: "active",
    bedAssignedAt: stay.bedAssignedAt ?? now,
  };

  writeBeds(beds);
  writeStays(stays);
  const assigned = stays[stayIndex]!;
  void apiAssignHospitalStayBed(stayId, bedId).then((remote) => {
    if (!remote) return;
    const list = readStays();
    const idx = list.findIndex((s) => s.id === stayId);
    if (idx >= 0) {
      list[idx] = normalizeStay(remote as unknown as Record<string, unknown>);
      writeStays(list);
    }
  });
  void import("@/core/billing/extended-hospital-billing").then(
    ({ syncRecurringHospitalizationBillsForStay }) =>
      syncRecurringHospitalizationBillsForStay(assigned),
  );
  return assigned;
}

export function dischargeStay(stayId: string): HospitalStay {
  const stays = readStays();
  const beds = readBeds();
  const stayIndex = stays.findIndex((s) => s.id === stayId);
  if (stayIndex < 0) throw new HospitalizationError("Séjour introuvable.");

  const stay = stays[stayIndex]!;
  void import("@/core/billing/extended-hospital-billing").then(
    ({ ensureHospitalizationDailyBill }) => ensureHospitalizationDailyBill(stay, new Date()),
  );

  const now = new Date().toISOString();

  if (stay.bedId) {
    const bedIndex = beds.findIndex((b) => b.id === stay.bedId);
    if (bedIndex >= 0) {
      beds[bedIndex] = {
        ...beds[bedIndex]!,
        status: "available",
        currentStayId: null,
      };
    }
  }

  stays[stayIndex] = {
    ...stay,
    status: "discharged",
    dischargedAt: now,
    bedId: null,
  };

  writeBeds(beds);
  writeStays(stays);
  const discharged = stays[stayIndex]!;
  void apiDischargeHospitalStay(stayId);
  return discharged;
}

export function countOccupiedBeds(): number {
  return listBedsWithLocation().filter((row) => row.bed.status === "occupied").length;
}

export function countPendingStays(): number {
  return listActiveStays().filter((s) => s.status === "pending").length;
}
